import Component from "vue-class-component"
import { Watch } from "vue-property-decorator"
import _ from "lodash"
import { faCheck, faCircleNotch, faTimes } from "@fortawesome/free-solid-svg-icons"

import { Container, Item, ItemTypeWrapper, ItemTypeWrapperWithIndex, Wemi } from "@skinsoft/types"
import { containerSelectors, itemSelectors } from "@skinsoft/selectors"

import { ReactiveVue } from "@skinsoft/reactive-vue"
import { ContainerCard } from "@skinsoft/entity-ui"
import { ListContentType } from "../list-content-type"
import { ContainerItemCard } from "./container-item-card"
import { SORT_ORDER_CHANGED, SORT_SELECTED, SortSelector } from "@skinsoft/ui2"

require('./container-item-list.scss')

@Component({
    components: {
        sContainerItemCard: ContainerItemCard,
        sContainerCard: ContainerCard,
        sSortSelector: SortSelector,
    },
    props: {
        items: Array,
        contentType: String,
        titleKey: String,
        updating: Boolean
    },
    template: require('./container-item-list.html')
})
export class ContainerItemList extends ReactiveVue {
    private items: ItemTypeWrapper[]
    private contentType: ListContentType
    private titleKey: string
    private updating: boolean

    private sortedItems: ItemTypeWrapperWithIndex[] = []
    private sortOptions: { value: number, text: string }[] = []
    private sortField: number | null
    private sortAscending: boolean = true

    public checkIcon = faCheck
    public crossIcon = faTimes
    public spinnerIcon = faCircleNotch

    SORT_SELECTED = SORT_SELECTED
    SORT_ORDER_CHANGED = SORT_ORDER_CHANGED

    private readonly ListContentType = ListContentType

    private mounted() {
        this.sortOptions = [
            {
                value: 1,
                text: this.$t("container.sorts.addedOrder") as string, // "ordre d'ajout"
            },
            {
                value: 2,
                text: this.$t("container.sorts.inventoryNumber") as string, // "identifiant"
            },
        ]

        this.applySort()
    }

    private getSubContainersSortFunction(): ((items: ItemTypeWrapper[]) => ItemTypeWrapper[]) | undefined {
        if(this.sortField === 2) {
            const getSortValue = (wrapper: ItemTypeWrapper): string => {
                let value
                if(wrapper.wrapperType === "Wemi") {
                    value = itemSelectors.inventoryNumber((wrapper.item as Wemi).item!)
                } else {
                    if(wrapper.itemType === "Container") {
                        value = itemSelectors.inventoryNumber(wrapper.item as Item)
                    } else {
                        value = containerSelectors.inventoryNumber(wrapper.item as Container)
                    }
                }

                return value.valueToSort ?? value
            }

            const sortAscending = this.sortAscending

            return (items: ItemTypeWrapper[]) => {
                // @ts-ignore toSorted
                return items.toSorted((itemWrapper1, itemWrapper2) => {
                    const value1: string = getSortValue(itemWrapper1)
                    const value2: string = getSortValue(itemWrapper2)
    
                    if(this.sortAscending) {
                        return value1.localeCompare(value2)
                    }
                    return value2.localeCompare(value1)
                })
            }
        }

        if(this.sortAscending) {
            return undefined
        }
        return (items: ItemTypeWrapper[]) => {
            // @ts-ignore toReversed
            return items.toReversed()
        }
    }

    private applySort() {
        let getSortValue: ((wrapper: ItemTypeWrapper) => string) | null = null

        switch(this.sortField) {
            case 2:
                getSortValue = (wrapper: ItemTypeWrapper): string => {
                    let value
                    if(wrapper.wrapperType === "Wemi") {
                        value = itemSelectors.inventoryNumber((wrapper.item as Wemi).item!)
                    } else {
                        if(wrapper.itemType === "Container") {
                            value = itemSelectors.inventoryNumber(wrapper.item as Item)
                        } else {
                            value = containerSelectors.inventoryNumber(wrapper.item as Container)
                        }
                    }

                    return value.valueToSort ?? value
                }
                break
        }

        this.sortedItems = this.items.map((wrapper, index) => {
            return {...wrapper, index: index}
        })

        if(getSortValue !== null) {
            // @ts-ignore toSorted
            this.sortedItems = this.sortedItems.sort((itemWrapper1, itemWrapper2) => {
                const value1: string = getSortValue!(itemWrapper1)
                const value2: string = getSortValue!(itemWrapper2)

                if(this.sortAscending) {
                    return value1.localeCompare(value2)
                }
                return value2.localeCompare(value1)
            })
        } else {
            if(this.sortAscending) {
                this.sortedItems = this.sortedItems
            } else {
                // @ts-ignore
                this.sortedItems = this.sortedItems.reverse()
            }
        }
    }

    private onLongPress(itemIndex: number) {
        const wrapper = this.sortedItems[itemIndex]
        if(wrapper && wrapper.index) {
            this.$emit('display-overlay', wrapper.index, this.contentType)
        }
    }

    private onCancel() {
        this.$emit('cancel-deletion')
    }

    private onDelete(item: ItemTypeWrapper) {
        this.$emit('delete', item)
    }

    private onSortFieldChange(sortField: number | null) {
        this.sortField = sortField
        this.applySort()   
    }

    private onSortOrderChanged(sortOrder: boolean) {
        this.sortAscending =  sortOrder
        this.applySort()
    }

    @Watch('items')
    onItemsChanged() {
        this.applySort()
    }
}